import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColumnDef, ColumnFiltersState } from '@tanstack/react-table';
import { debounce } from 'lodash';
import type { DataTableColumnMeta, ExtraFilter, FilterMode, ServerFilterVariables } from '../types';

type UseDataTableFiltersOptions<TData, TFilter extends Record<string, unknown>> = {
    columns: ColumnDef<TData, unknown>[];
    filterMode: FilterMode;
    enableUrlSync?: boolean;
    defaultFilters?: Partial<TFilter>;
    // Scope imposé, tenu HORS de `filterState` : il ne doit ni allumer « Effacer », ni disparaître à la
    // réinitialisation, ni dépendre de l'URL pour survivre à un rechargement.
    pinnedFilters?: Partial<TFilter>;
    searchFilterKey?: keyof TFilter;
    refetch?: (variables: ServerFilterVariables<TFilter>) => Promise<unknown>;
    // Standalone (non-column) filters that still participate in URL sync.
    extraFilters?: ExtraFilter[];
};

export function useDataTableFilters<TData, TFilter extends Record<string, unknown>>({
    columns,
    filterMode,
    enableUrlSync = false,
    defaultFilters,
    pinnedFilters,
    searchFilterKey,
    refetch,
    extraFilters,
}: UseDataTableFiltersOptions<TData, TFilter>) {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const searchParams = enableUrlSync ? urlSearchParams : null;
    const setSearchParams = enableUrlSync ? setUrlSearchParams : null;

    // Extract filter configs from column meta
    const filterConfigs = useMemo(() => {
        const configs: {
            columnId: string;
            filterKey: string;
            type: string;
            defaultValue?: unknown;
        }[] = [];
        for (const col of columns) {
            const meta = col.meta as DataTableColumnMeta<TFilter> | undefined;
            if (meta?.filter) {
                const columnId = 'accessorKey' in col ? String(col.accessorKey) : 'id' in col ? String(col.id) : '';
                const filterKey = meta.filter.filterKey ? String(meta.filter.filterKey) : columnId;
                configs.push({
                    columnId,
                    filterKey,
                    type: meta.filter.type,
                    defaultValue: meta.filter.defaultValue,
                });
            }
        }
        return configs;
    }, [columns]);

    // Parse URL params into filter state
    const parseUrlParams = useCallback(
        (params: URLSearchParams): Partial<TFilter> => {
            const state: Record<string, unknown> = {};

            const searchKey = String(searchFilterKey || 'searchValue');
            if (params.has(searchKey)) {
                state[searchKey] = params.get(searchKey) || '';
            }

            for (const extra of extraFilters ?? []) {
                if (extra.type === 'text') {
                    if (params.has(extra.key)) state[extra.key] = params.get(extra.key) || '';
                } else {
                    const values = params.getAll(extra.key);
                    if (values.length > 0) state[extra.key] = values;
                }
            }

            for (const config of filterConfigs) {
                const values = params.getAll(config.filterKey);
                if (values.length === 0) continue;

                switch (config.type) {
                    case 'number': {
                        const parsed = parseInt(values[0], 10);
                        state[config.filterKey] = Number.isNaN(parsed) ? null : parsed;
                        break;
                    }
                    case 'boolean':
                        state[config.filterKey] = values[0] === 'true';
                        break;
                    case 'multi-select':
                        state[config.filterKey] = values;
                        break;
                    case 'select':
                        state[config.filterKey] = values;
                        break;
                    case 'date':
                        state[config.filterKey] = values[0];
                        break;
                    case 'date-range':
                    case 'number-range': {
                        const obj: Record<string, number> = {};
                        for (const v of values) {
                            const [op, ts] = v.split(':');
                            if (op && ts) obj[op] = parseFloat(ts);
                        }
                        if (Object.keys(obj).length > 0) state[config.filterKey] = obj;
                        break;
                    }
                    default:
                        state[config.filterKey] = values[0];
                }
            }

            return state as Partial<TFilter>;
        },
        [filterConfigs, searchFilterKey, extraFilters],
    );

    // Build URL params from filter state
    const buildUrlParams = useCallback((state: Partial<TFilter>): Record<string, string | string[]> => {
        const params: Record<string, string | string[]> = {};

        for (const [key, value] of Object.entries(state)) {
            if (value === undefined || value === null || value === '') continue;
            if (Array.isArray(value)) {
                if (value.length > 0) params[key] = value;
            } else if (typeof value === 'object') {
                // date-range: { gte: timestamp, lte: timestamp } → ["gte:123", "lte:456"]
                const parts = Object.entries(value as Record<string, number>)
                    .filter(([, v]) => v != null)
                    .map(([op, v]) => `${op}:${v}`);
                if (parts.length > 0) params[key] = parts;
            } else if (typeof value === 'boolean') {
                params[key] = String(value);
            } else {
                params[key] = String(value);
            }
        }

        return params;
    }, []);

    // Initialize filter state
    const [filterState, setFilterState] = useState<Partial<TFilter>>(() => {
        if (enableUrlSync && searchParams) {
            const fromUrl = parseUrlParams(searchParams);
            if (Object.keys(fromUrl).length > 0) return fromUrl;
        }
        if (defaultFilters) return defaultFilters;

        // Apply default values from column configs
        const defaults: Record<string, unknown> = {};
        for (const config of filterConfigs) {
            if (config.defaultValue !== undefined) {
                defaults[config.filterKey] = config.defaultValue;
            }
        }
        return (Object.keys(defaults).length > 0 ? defaults : {}) as Partial<TFilter>;
    });

    // Ce qui part au serveur : le scope imposé, puis les filtres de l'utilisateur (qui peuvent l'affiner,
    // jamais le lever). `filterState` reste la vue « ce que l'utilisateur a posé », seule à piloter l'UI.
    // Sérialisé pour comparer le scope PAR VALEUR : les appelants le construisent en ligne (`{ siteId }`),
    // une identité d'objet neuve à chaque rendu relancerait un refetch en boucle.
    const pinnedFiltersKey = JSON.stringify(pinnedFilters ?? {});
    const serverFilters = useMemo(
        () => ({ ...(JSON.parse(pinnedFiltersKey) as Partial<TFilter>), ...filterState }),
        [pinnedFiltersKey, filterState],
    );

    // Search value (separate for convenience)
    const searchKey = String(searchFilterKey || 'searchValue');
    const searchValue = (filterState[searchKey as keyof TFilter] as string) || '';

    // Sync URL on filter change
    useEffect(() => {
        if (enableUrlSync && setSearchParams) {
            // Le scope est réécrit dans l'URL : au rechargement, `parseUrlParams` ignore les clés qui ne
            // correspondent à aucune colonne, c'est le parent qui le relit et le repasse en `pinnedFilters`.
            const params = buildUrlParams(serverFilters);
            const newSearchParams = new URLSearchParams();
            for (const [key, value] of Object.entries(params)) {
                if (Array.isArray(value)) {
                    for (const v of value) {
                        newSearchParams.append(key, v);
                    }
                } else {
                    newSearchParams.set(key, value);
                }
            }
            setSearchParams(newSearchParams, { replace: true });
        }
    }, [serverFilters, enableUrlSync, setSearchParams, buildUrlParams]);

    // Debounced refetch for server mode
    // Use a ref so the debounced function is stable even if the refetch prop changes identity
    const refetchRef = useRef(refetch);
    refetchRef.current = refetch;

    const debouncedRefetch = useMemo(() => {
        if (filterMode !== 'server') return null;
        return debounce((sortBy: string | undefined, sortOrder: string | undefined, filters: Partial<TFilter>) => {
            refetchRef.current?.({ sortBy, sortOrder, filters });
        }, 300);
    }, [filterMode]);

    // Update a single filter
    const updateFilter = useCallback((key: string, value: unknown) => {
        setFilterState((prev) => {
            if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
                const next = { ...prev };
                delete next[key as keyof TFilter];
                return next;
            }
            return { ...prev, [key]: value };
        });
    }, []);

    // Update search value
    const updateSearch = useCallback(
        (value: string) => {
            updateFilter(searchKey, value || undefined);
        },
        [searchKey, updateFilter],
    );

    // Clear all filters
    const clearFilters = useCallback(() => {
        setFilterState({} as Partial<TFilter>);
    }, []);

    // Convert to TanStack ColumnFiltersState (for client mode)
    const columnFiltersState: ColumnFiltersState = useMemo(() => {
        if (filterMode !== 'client') return [];
        const filters: ColumnFiltersState = [];

        for (const config of filterConfigs) {
            const value = filterState[config.filterKey as keyof TFilter];
            if (value === undefined || value === null || value === '') continue;
            filters.push({ id: config.columnId, value });
        }

        return filters;
    }, [filterMode, filterConfigs, filterState]);

    // Global filter for client-side search
    const globalFilter = filterMode === 'client' ? searchValue : undefined;

    return {
        filterState,
        serverFilters,
        setFilterState,
        searchValue,
        updateSearch,
        updateFilter,
        clearFilters,
        columnFiltersState,
        globalFilter,
        debouncedRefetch,
        filterConfigs,
    };
}
